import { motion } from "framer-motion";
import { BadgeCheck, Users, Flame } from "lucide-react";
import SectionHeading from "./SectionHeading";


const PILLARS = [
  {
    icon: BadgeCheck,
    title: "NHCA Certified",
    text: "Certified Dietitian & Wellness Coach — National Health Care Academy.",
  },
  { icon: Users, title: "200+ Clients", text: "Coached one-on-one across fat loss, thyroid, PCOS and gut health." },
  { icon: Flame, title: "7 Years In Practice", text: "Seven years of whole-food first coaching — zero unnecessary supplements." },
];

const CREDENTIALS = [
  { k: "Certification", v: "NHCA · National Health Care Academy" },
  { k: "Clients Coached", v: "200+" },
  { k: "Years In Practice", v: "7" },
  { k: "Method", v: "Whole-Food First" },
];

export default function About() {
  return (
    <section id="about" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <SectionHeading
          eyebrow="About"
          title="Built On Food, Not Products"
          sub="Nikhil Shrirao is an NHCA Certified Dietitian & Nutritionist — certified by the National Health Care Academy — with 7 years of practice and 200+ clients coached one-on-one. Every result here was built on whole food first — with clinical supplementation only where blood work demands it."
        />
        <div className="mt-14 grid lg:grid-cols-12 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-12 rounded-3xl border border-line bg-panel p-8 lg:p-12 transition-all duration-300 hover:border-accent/40 hover:shadow-glow-emerald"
            data-testid="about-main-card"
          >
            <p className="font-display text-2xl sm:text-3xl font-bold text-white leading-snug">
              "You don't need a product to buy. You need the right food, in the right quantity, at the right time."
            </p>
            <p className="mt-6 text-slate-400 leading-relaxed">
              Nikhil's coaching is a whole-food first practice. No commercial meal replacement shakes, no synthetic
              MLM powders and no blind fat burners — targeted, clinical supplementation is recommended only when a deep
              medical history or your blood test markers genuinely demand it. What you get instead is a low
              glycaemic index, zero-added-sugar protocol built on Indian home food, with protein prescribed in
              grams, cold-pressed oils and ghee instead of refined oil, and daily WhatsApp accountability. He works
              with people carrying real health problems — thyroid, PCOS, insulin resistance, stubborn fat, poor
              energy — and the plan is rebuilt every 15 days as your body responds.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {["Thyroid", "PCOS / PCOD", "Insulin Resistance", "Gut Health", "Fat Loss"].map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-1.5 rounded-full bg-ink border border-line font-mono text-xs uppercase tracking-widest text-slate-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="lg:col-span-4 rounded-3xl border border-line bg-panel p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-glow-gold"
              data-testid={`about-pillar-${i}`}
            >
              <p.icon className="h-7 w-7 text-emerald-400" />
              <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-white">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-px rounded-3xl border border-line bg-line overflow-hidden"
          data-testid="about-credentials-strip"
        >
          {CREDENTIALS.map((c) => (
            <div key={c.k} className="bg-panel px-6 py-7">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{c.k}</p>
              <p className="mt-2 font-display text-lg sm:text-xl font-extrabold uppercase text-white leading-tight">
                {c.v}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}